import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/context/AuthContext";
import {
  LayoutDashboard,
  CalendarRange,
  CalendarDays,
  CreditCard,
  BarChart3,
  FileBarChart,
  Building2,
  LogOut,
  Plus,
  Menu,
  X,
  Search,
  UserRound,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AccountSettings from "@/components/AccountSettings";
import { useCompany } from "@/context/CompanyContext";
import {
  Select as USelect,
  SelectContent as USelectContent,
  SelectItem as USelectItem,
  SelectTrigger as USelectTrigger,
  SelectValue as USelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/bookings", label: "Bookings", icon: CalendarRange, testid: "nav-bookings" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/tasks", label: "Cleaning & Maintenance", icon: Wrench, testid: "nav-tasks" },
  { to: "/payments", label: "Payments", icon: CreditCard, testid: "nav-payments" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", adminOnly: true },
  { to: "/monthly-report", label: "Monthly Report", icon: FileBarChart, testid: "nav-monthly" },
];

function CompanyPicker({ mobile }) {
  const { companies, company, setCompany, refresh } = useCompany();
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const addCompany = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const r = await api.post("/companies", { name });
      toast.success(`Added ${r.data.name}`);
      setNewName(""); setAddOpen(false);
      await refresh();
      setCompany(r.data.name);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add company");
    }
  };

  return (
    <div className="mb-3 space-y-2">
      <label className="label-eyebrow px-1 flex items-center gap-1"><Building2 className="w-3 h-3" /> Company</label>
      <USelect value={company} onValueChange={setCompany}>
        <USelectTrigger className="rounded-sm border-slate-300 h-9 text-sm" data-testid={mobile ? "mobile-company-select" : "company-select"}>
          <USelectValue />
        </USelectTrigger>
        <USelectContent>
          {companies.map((c) => (
            <USelectItem key={c.id || c.name} value={c.name}>{c.name}</USelectItem>
          ))}
          {companies.length === 0 && <USelectItem value="Flexistay Luton Limited">Flexistay Luton Limited</USelectItem>}
        </USelectContent>
      </USelect>
      {!addOpen ? (
        <button
          onClick={() => setAddOpen(true)}
          className="text-[10px] uppercase tracking-wider text-blue-900 hover:underline px-1"
          data-testid="add-company-toggle"
        >
          + Add company
        </button>
      ) : (
        <div className="flex gap-1">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Company name"
            className="h-8 rounded-sm text-sm"
            data-testid="add-company-input"
          />
          <Button size="sm" onClick={addCompany} className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white h-8" data-testid="add-company-submit">Add</Button>
          <Button size="sm" variant="outline" onClick={() => { setAddOpen(false); setNewName(""); }} className="rounded-sm h-8">×</Button>
        </div>
      )}
    </div>
  );
}

function Sidebar({ mobile, user, onClose, onNewBooking, onLogout, onOpenAccount, globalQ, setGlobalQ, onSubmitSearch }) {
  const isAdmin = (user?.role || "ADMIN").toUpperCase() === "ADMIN";
  return (
    <aside
      className={`${
        mobile ? "w-72" : "hidden lg:flex w-64 sticky top-0"
      } border-r border-slate-200 bg-white flex-col h-screen z-40 print:hidden`}
    >
      <div className="p-5 lg:p-6 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="iRentals Serviced Accommodation" className="w-10 h-10 object-contain" />
          <div>
            <div className="font-heading text-lg font-semibold tracking-tight text-slate-900">iRentals</div>
            <div className="text-[10px] label-eyebrow">Serviced Accommodation</div>
          </div>
        </div>
        {mobile && (
          <button
            onClick={onClose}
            className="p-1 text-slate-500 hover:text-slate-900"
            data-testid="mobile-nav-close"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <CompanyPicker mobile={mobile} />
        <form onSubmit={onSubmitSearch} className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            value={globalQ}
            onChange={(e) => setGlobalQ(e.target.value)}
            placeholder="Search reference or guest…"
            className="pl-9 pr-3 h-9 rounded-sm border-slate-300 focus:border-blue-900 focus:ring-blue-900 text-sm"
            data-testid={mobile ? "mobile-global-search" : "global-search"}
            aria-label="Search bookings"
          />
        </form>
        {nav.filter((n) => !n.adminOnly || isAdmin).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                isActive ? "bg-blue-900 text-white" : "text-slate-700 hover:bg-slate-100"
              }`
            }
          >
            <n.icon className="w-4 h-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-slate-200 space-y-2">
        <Button
          onClick={onNewBooking}
          disabled={!isAdmin}
          className={`w-full rounded-sm text-white active:scale-[0.98] transition-transform ${isAdmin ? "bg-blue-900 hover:bg-blue-800" : "bg-slate-400 cursor-not-allowed"}`}
          data-testid={mobile ? "mobile-new-booking-button" : "new-booking-button"}
          title={isAdmin ? "" : "View-only role"}
        >
          <Plus className="w-4 h-4 mr-2" /> New Booking
        </Button>
        <div className="flex items-center justify-between px-2 pt-1">
          <div className="min-w-0">
            <div className="text-xs font-semibold truncate text-slate-900">{user?.name || user?.email}</div>
            <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onOpenAccount}
              className="text-slate-500 hover:text-blue-900 transition-colors p-1"
              data-testid={mobile ? "mobile-account-button" : "account-button"}
              title="Account settings"
            >
              <UserRound className="w-4 h-4" />
            </button>
            <button
              onClick={onLogout}
              className="text-slate-500 hover:text-blue-900 transition-colors p-1"
              data-testid="logout-button"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default function Layout() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [globalQ, setGlobalQ] = useState("");
  const [accountOpen, setAccountOpen] = useState(false);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const q = globalQ.trim();
    setOpen(false);
    navigate(q ? `/bookings?q=${encodeURIComponent(q)}` : "/bookings");
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleNewBooking = () => {
    setOpen(false);
    navigate("/bookings/new");
  };

  const sidebarProps = {
    user,
    onClose: () => setOpen(false),
    onNewBooking: handleNewBooking,
    onLogout: handleLogout,
    onOpenAccount: () => { setOpen(false); setAccountOpen(true); },
    globalQ,
    setGlobalQ,
    onSubmitSearch: submitSearch,
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] lg:flex print:block print:bg-white">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-white border-b border-slate-200 px-4 h-14 print:hidden">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setOpen(true)}
            className="p-1.5 text-slate-700 hover:bg-slate-100 rounded-sm"
            data-testid="mobile-nav-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="iRentals" className="w-8 h-8 object-contain" />
            <span className="font-heading text-base font-semibold tracking-tight text-slate-900">iRentals</span>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => navigate("/bookings/new")}
          className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white h-8"
          data-testid="mobile-add-booking-header"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> New
        </Button>
      </header>

      <Sidebar {...sidebarProps} mobile={false} />

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex print:hidden">
          <div
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-overlay"
          />
          <div className="relative">
            <Sidebar {...sidebarProps} mobile={true} />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>

      <AccountSettings
        open={accountOpen}
        onOpenChange={setAccountOpen}
        user={user}
        onUserUpdated={(u) => setUser?.(u)}
      />
    </div>
  );
}
